import { LibraryItemDetailCourseSettingsRightPane_CourseFragment } from "./course/build/CourseSettingsRightPane.generated";
import { format, parseISO } from "date-fns";
import { isNotNullOrUndefined } from "@src/utils/typeguards";
import { pluralize } from "@src/utils/strings";
import { HeightAndWidth } from "@src/hooks/useRefHeightAndWidth";
import { ViewMode } from "./hooks/useViewModeQueryParams";
import { AutomationContentType } from "../sidebars/shared/constants";
import { LibraryItemDetailPage_LibraryItemFragment } from "./LibraryItemDetailPageContainer.generated";
import moduleStartsWithSectionBreak from "./module/utils/moduleStartsWithSectionBreak";
import { Atom, useAtomValue } from "jotai";
import { membershipSectionsAtom } from "./module/atoms";
import { CourseScreen } from "./course/hooks/useCourseScreensAtom";
import { selectAtom } from "jotai/utils";
import { useMemo } from "react";
import { isEqual } from "lodash";
import { PathAssignmentTable_AssignmentFragment } from "@src/fragments.generated";
import { PromptType } from "@src/types.generated";
import {
  CourseContentPromptFragment,
  ResponseOptionFragment,
} from "@src/components/libraryItemDetailPages/course/build/CourseBuildContent.generated";
import { PromptErrorType } from "@src/components/libraryItemDetailPages/course/types/validation";

export const GAP_BETWEEN_RIGHT_PANE_SETTINGS = 14;

export const showSectionCardsAtom = selectAtom(
  membershipSectionsAtom,
  (membershipSections) => {
    return Boolean(
      membershipSections &&
        membershipSections?.length >= 1 &&
        moduleStartsWithSectionBreak(membershipSections),
    );
  },
  isEqual,
);

export const formatCourseDueDateForDisplay = (
  course: LibraryItemDetailCourseSettingsRightPane_CourseFragment | undefined,
): string => {
  return formatDueDateForDisplay(
    course?.libraryItem.dueDate,
    course?.libraryItem.dueDateDays,
  );
};

export const formatDueDateForDisplay = (
  dueDate?: string | null,
  dueDateDays?: number | null,
): string => {
  if (dueDate) {
    const date = parseISO(dueDate);
    return format(date, "MMMM d");
  } else if (isNotNullOrUndefined(dueDateDays)) {
    return `${dueDateDays} ${pluralize("day", dueDateDays)}`;
  } else {
    return "None";
  }
};

export interface BuilderPhoneDimensions extends HeightAndWidth {
  gap: number;
  x: number;
  y: number;
  height: number;
  width: number;
}
export const getViewModePhoneDimensions = (
  viewMode: ViewMode,
): BuilderPhoneDimensions | undefined => {
  if (viewMode === "small-screens") {
    return { x: 0, y: 0, height: 275, width: 275, gap: 4 };
  } else if (viewMode === "medium-screens") {
    return { x: 0, y: 0, height: 410, width: 410, gap: 16 };
  }
  return undefined;
};

export const getAutomationContentTypeOfLibraryItem = (
  libraryItem: LibraryItemDetailPage_LibraryItemFragment,
): AutomationContentType => {
  if (libraryItem.course) {
    return "Course";
  } else if (libraryItem.path) {
    return "Path";
  } else if (libraryItem.trainingResource) {
    return "TrainingResource";
  } else {
    return "OrgPremiumContentConfig";
  }
};

export const findFirstEmojiInString = (string: string): string | undefined => {
  const regex =
    /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|\ud83c[\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|\ud83c[\ude32-\ude3a]|\ud83c[\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|[\u2190-\u21ff])/g;
  const emojisInText = string.match(regex) || [];
  if (emojisInText.length >= 1) {
    return emojisInText[0];
  }
};

export const useGetScreenIndex = (
  screenAtom: Atom<CourseScreen | undefined>,
): number => {
  return useAtomValue(
    useMemo(() => {
      return selectAtom(
        screenAtom,
        (screen) => {
          if (!screen) return 0;
          if ("promptScreen" in screen) {
            const promptScreen = screen.promptScreen;
            return (
              screen.promptScreen.lesson.prompts.findIndex(
                (prompt) => prompt.uuid === promptScreen.prompt?.uuid,
              ) + 1
            );
          } else if ("homeScreen" in screen) {
            return 0;
          } else if ("lessonScreen" in screen) {
            return 0;
          }
          return 0;
        },
        isEqual,
      );
    }, [screenAtom]),
  );
};

export const getPathCertificateUrl = (
  assignment: PathAssignmentTable_AssignmentFragment,
): string | null => {
  const numInstances = assignment.pathInstances.length;
  if (numInstances === 0) {
    return null;
  }
  const pi = assignment.pathInstances[numInstances - 1];
  return pi?.certificateUrl || null;
};

export const mediaHiddenForPromptType = (promptType: PromptType): boolean => {
  return (
    promptType === PromptType.TitleScreen ||
    promptType === PromptType.Matching ||
    promptType === PromptType.Ordering
  );
};

export const promptTypeHas2InitialResponseOptions = (
  promptType: PromptType,
): boolean => {
  return (
    promptType === PromptType.MultipleChoice ||
    promptType === PromptType.ImageChoice ||
    promptType === PromptType.Matching ||
    promptType === PromptType.Ordering
  );
};

export const getResponseOptionHasValidationError = (
  ro: ResponseOptionFragment,
  promptValidationErrors: PromptErrorType[],
  prompt: CourseContentPromptFragment,
): boolean => {
  if (
    !!promptValidationErrors?.includes(
      PromptErrorType.MISSING_RESPONSE_OPTION,
    ) &&
    ro.answer.en.length === 0
  ) {
    return true;
  }
  if (
    promptValidationErrors?.includes(PromptErrorType.MISSING_MATCH_ITEM_TEXT) &&
    !prompt.imageOnlyMatchItems &&
    ro.matchItem?.text.en.length === 0
  ) {
    return true;
  }
  if (
    promptValidationErrors?.includes(
      PromptErrorType.MISSING_MATCH_ITEM_IMAGE,
    ) &&
    prompt.imageOnlyMatchItems &&
    !ro.matchItem?.mediaId
  ) {
    return true;
  }
  return false;
};

export const showResponseOptionMissingTextErrorText = (
  promptValidationErrors: PromptErrorType[],
): string | null => {
  if (
    promptValidationErrors?.includes(PromptErrorType.MISSING_MATCH_ITEM_TEXT) ||
    promptValidationErrors?.includes(PromptErrorType.MISSING_RESPONSE_OPTION)
  ) {
    return "Please add missing text";
  }
  return null;
};

export const showResponseOptionMissingMatchItemImageErrorText = (
  promptValidationErrors: PromptErrorType[],
): string | null => {
  if (
    promptValidationErrors?.includes(PromptErrorType.MISSING_MATCH_ITEM_IMAGE)
  ) {
    return "Please add an image for this option";
  }
  return null;
};
